import {
  GameSimulation,
  type SnapshotOptions,
} from "./phase-three-simulation";
import {
  MAX_PLAYERS,
  RECONNECT_WINDOW_MS,
  type ClientMessage,
  type DifficultyId,
  type PerformanceMetrics,
  type PublicRoomState,
  type ServerMessage,
} from "./phase-three-types";

export interface GameSocket {
  readonly readyState: number;
  send(payload: string): void;
  close(code?: number, reason?: string): void;
}

const SOCKET_OPEN = 1;

interface PlayerSession {
  playerId: string;
  token: string;
  socket?: GameSocket;
  disconnectedAt?: number;
  bytesSinceSample: number;
  lastKbps: number;
}

export interface JoinResult {
  playerId: string;
  token: string;
  state: PublicRoomState;
}

export interface RoomOptions {
  code: string;
  durationMs?: number;
  seed?: string;
}

export class GameRoom {
  readonly code: string;
  readonly simulation: GameSimulation;
  hostId = "";

  private readonly sessions = new Map<string, PlayerSession>();
  private readonly tickSamples: number[] = [];
  private lastSnapshotAt = 0;
  private lastEnemyAnchorAt = 0;
  private lastWorldSnapshotAt = 0;
  private lastDefenseAnchorAt = 0;
  private lastMetricSampleAt = Date.now();
  private metrics: PerformanceMetrics = {
    tickP95Ms: 0,
    outgoingKbpsPerClient: 0,
    enemyCount: 0,
    abilityEntityCount: 0,
  };

  constructor(options: RoomOptions) {
    this.code = options.code;
    this.simulation = new GameSimulation({
      seed: options.seed ?? `${options.code}-${Date.now()}`,
      durationMs: options.durationMs,
    });
  }

  get playerCount(): number {
    return this.sessions.size;
  }

  get connectedCount(): number {
    return [...this.sessions.values()].filter((session) => session.socket).length;
  }

  getPlaytestState(playerId: string, token: string): PublicRoomState {
    const session = this.sessions.get(playerId);
    if (!session || session.token !== token) {
      throw new Error("测试报告会话凭证无效。");
    }
    return this.snapshot(false, {}, playerId);
  }

  join(name: string, socket: GameSocket): JoinResult {
    if (this.sessions.size >= MAX_PLAYERS) {
      throw new Error("房间已满。");
    }
    const playerId = createRandomId();
    const token = createRandomToken();
    const added = this.simulation.addPlayer(playerId, name);
    if (!added.ok) {
      throw new Error(added.error);
    }
    const session: PlayerSession = {
      playerId,
      token,
      socket,
      bytesSinceSample: 0,
      lastKbps: 0,
    };
    this.sessions.set(playerId, session);
    if (!this.hostId) {
      this.hostId = playerId;
    }
    return {
      playerId,
      token,
      state: this.snapshot(true, {}, playerId),
    };
  }

  reconnect(
    playerId: string,
    token: string,
    socket: GameSocket,
    now = Date.now(),
  ): JoinResult {
    const session = this.sessions.get(playerId);
    if (!session || session.token !== token) {
      throw new Error("重连凭证无效。");
    }
    if (
      session.disconnectedAt !== undefined &&
      now - session.disconnectedAt > RECONNECT_WINDOW_MS
    ) {
      throw new Error("90 秒重连窗口已结束。");
    }
    session.socket?.close(4001, "Replaced by reconnect");
    session.socket = socket;
    session.disconnectedAt = undefined;
    this.simulation.setPlayerConnected(playerId, true);
    return {
      playerId,
      token: session.token,
      state: this.snapshot(true, {}, playerId),
    };
  }

  disconnect(playerId: string, socket: GameSocket, now = Date.now()): void {
    const session = this.sessions.get(playerId);
    if (!session || session.socket !== socket) {
      return;
    }
    session.socket = undefined;
    session.disconnectedAt = now;
    this.simulation.setPlayerConnected(playerId, false);
    this.broadcast({
      type: "notice",
      message: `${this.simulation.players.get(playerId)?.name ?? "玩家"} 已断线，AI 接管 90 秒`,
    });
    this.broadcastSnapshot(false);
  }

  handleMessage(playerId: string, message: ClientMessage): void {
    switch (message.type) {
      case "ready": {
        const result = this.simulation.setReady(playerId, message.ready);
        if (!result.ok) {
          this.sendError(playerId, "READY_REJECTED", result.error);
        }
        this.broadcastSnapshot(false);
        break;
      }
      case "start": {
        if (playerId !== this.hostId) {
          this.sendError(playerId, "HOST_ONLY", "只有房主可以开始对局。");
          break;
        }
        const result = this.simulation.start(message.difficulty as DifficultyId);
        if (!result.ok) {
          this.sendError(playerId, "START_REJECTED", result.error);
        }
        this.broadcastSnapshot(false);
        break;
      }
      case "input":
        this.simulation.setInput(
          playerId,
          message.sequence,
          clamp(message.x, -1, 1),
          clamp(message.y, -1, 1),
        );
        break;
      case "requestOffer": {
        const result = this.simulation.requestOffer(playerId, message.slot);
        if (result.ok) {
          this.send(playerId, { type: "offer", offer: result.value });
          this.broadcastSnapshot(false);
        } else {
          this.sendError(playerId, "OFFER_REJECTED", result.error);
        }
        break;
      }
      case "requestReforge": {
        const result = this.simulation.requestReforge(playerId, message.slot);
        if (result.ok) {
          this.send(playerId, { type: "offer", offer: result.value });
          this.broadcastSnapshot(false);
        } else {
          this.sendError(playerId, "REFORGE_REJECTED", result.error);
        }
        break;
      }
      case "rerollOffer": {
        const result = this.simulation.rerollOffer(playerId, message.offerId);
        if (result.ok) {
          this.send(playerId, { type: "offer", offer: result.value });
          this.broadcastSnapshot(false);
        } else {
          this.sendError(playerId, "REROLL_REJECTED", result.error);
        }
        break;
      }
      case "cancelOffer": {
        const result = this.simulation.cancelOffer(playerId, message.offerId);
        if (result.ok) {
          this.send(playerId, { type: "offerCleared" });
          this.broadcastSnapshot(false);
        } else {
          this.sendError(playerId, "CANCEL_REJECTED", result.error);
        }
        break;
      }
      case "selectOffer": {
        const result = this.simulation.selectOffer(
          playerId,
          message.offerId,
          message.moduleId,
        );
        if (result.ok) {
          this.send(playerId, { type: "offerCleared" });
          this.broadcastSnapshot(false);
        } else {
          this.sendError(playerId, "SELECTION_REJECTED", result.error);
        }
        break;
      }
      case "ascendAbility": {
        const result = this.simulation.ascendAbility(playerId, message.slot);
        if (!result.ok) {
          this.sendError(playerId, "ASCENSION_REJECTED", result.error);
        }
        this.broadcastSnapshot(false);
        break;
      }
      case "investTalent": {
        const result = this.simulation.investTalent(playerId, message.talentId);
        if (!result.ok) {
          this.sendError(playerId, "TALENT_REJECTED", result.error);
        }
        this.broadcastSnapshot(false);
        break;
      }
      case "mineTransit":
      case "mineWork":
      case "minePickup":
      case "mineSurvey": {
        this.sendError(
          playerId,
          "FEATURE_ARCHIVED",
          "P4.11重型采矿动作已封存（ARCHIVED_MINING）；P4.13地表采矿会在矿脉范围内自动结算，无需动作请求。",
        );
        break;
      }
      case "excavate": {
        const result = this.simulation.excavate(playerId, message.nodeId);
        if (!result.ok) {
          this.sendError(playerId, "FEATURE_ARCHIVED", result.error);
        }
        break;
      }
      case "aidResource": {
        const result = this.simulation.aidResource(
          playerId,
          message.targetPlayerId,
          message.resource,
          message.amount,
        );
        if (!result.ok) {
          this.sendError(playerId, "RESOURCE_AID_REJECTED", result.error);
        }
        this.broadcastSnapshot(false);
        break;
      }
      case "donateCore": {
        const result = this.simulation.donateCore(
          playerId,
          message.targetPlayerId,
          message.amount,
        );
        if (!result.ok) {
          this.sendError(playerId, "DONATION_REJECTED", result.error);
        }
        this.broadcastSnapshot(false);
        break;
      }
      case "investTeamTech": {
        const result = this.simulation.investTeamTech(
          playerId,
          message.techId,
        );
        if (!result.ok) {
          this.sendError(playerId, "TEAM_TECH_REJECTED", result.error);
        }
        this.broadcastSnapshot(false);
        break;
      }
      case "teamSignal": {
        const result = this.simulation.createTeamSignal(
          playerId,
          message.kind,
          message.laneId,
        );
        if (!result.ok) {
          this.sendError(playerId, "SIGNAL_REJECTED", result.error);
        }
        this.broadcastSnapshot(false);
        break;
      }
      case "ping":
        this.send(playerId, {
          type: "pong",
          clientTime: message.clientTime,
          serverTime: Date.now(),
        });
        break;
      case "create":
      case "join":
      case "reconnect":
        this.sendError(playerId, "ALREADY_CONNECTED", "连接已经绑定到房间。");
        break;
    }
  }

  tick(now: number, deltaMs: number): void {
    const started = performance.now();
    this.simulation.tick(deltaMs);
    this.tickSamples.push(performance.now() - started);
    if (this.tickSamples.length > 1_200) {
      this.tickSamples.splice(0, this.tickSamples.length - 1_200);
    }

    for (const event of this.simulation.drainEvents()) {
      if (
        event.type === "surfaceMining" ||
        event.type === "surfaceMiningDevour"
      ) {
        this.send(event.playerId, event);
      } else {
        this.broadcast(event);
      }
    }

    if (now - this.lastMetricSampleAt >= 1_000) {
      this.sampleMetrics(now - this.lastMetricSampleAt);
      this.lastMetricSampleAt = now;
    }
    if (now - this.lastSnapshotAt >= 200) {
      const includeWorldState = now - this.lastWorldSnapshotAt >= 1_000;
      const includeEnemyAnchors = now - this.lastEnemyAnchorAt >= 1_000;
      const includeDefenseAnchors = now - this.lastDefenseAnchorAt >= 1_000;
      this.broadcastSnapshot(false, {
        includeWorldState,
        includeDefenses: false,
        // Full world snapshots already contain positions and health. Sending
        // anchors in the same packet duplicates the largest high-count lists.
        includeMotionAnchors: !includeWorldState,
        includeEnemyAnchors,
        includeDefenseAnchors,
      });
      this.lastSnapshotAt = now;
      if (includeWorldState) {
        this.lastWorldSnapshotAt = now;
      }
      if (includeEnemyAnchors) {
        this.lastEnemyAnchorAt = now;
      }
      if (includeDefenseAnchors) {
        this.lastDefenseAnchorAt = now;
      }
    }
  }

  sendWelcome(
    playerId: string,
    token: string,
    resumed: boolean,
  ): void {
    const offer = this.simulation.getPendingOffer(playerId);
    this.send(playerId, {
      type: "welcome",
      roomCode: this.code,
      playerId,
      token,
      resumed,
      state: this.snapshot(true, {}, playerId),
      offer,
    });
  }

  isReconnectExpired(playerId: string, now = Date.now()): boolean {
    const session = this.sessions.get(playerId);
    return Boolean(
      session?.disconnectedAt !== undefined &&
        now - session.disconnectedAt > RECONNECT_WINDOW_MS,
    );
  }

  canDispose(now = Date.now()): boolean {
    if (this.connectedCount > 0 || this.sessions.size === 0) {
      return false;
    }
    return [...this.sessions.values()].every(
      (session) =>
        session.disconnectedAt !== undefined &&
        now - session.disconnectedAt > RECONNECT_WINDOW_MS,
    );
  }

  private snapshot(
    includeEnemies: boolean,
    options: SnapshotOptions = {},
    viewerPlayerId?: string,
  ): PublicRoomState {
    return this.simulation.getSnapshot(
      this.code,
      this.hostId,
      Date.now(),
      includeEnemies,
      this.metrics,
      { ...options, viewerPlayerId },
    );
  }

  private broadcastSnapshot(
    includeEnemies: boolean,
    options: SnapshotOptions = {},
  ): void {
    for (const session of this.sessions.values()) {
      this.sendPayload(
        session,
        JSON.stringify({
          type: "snapshot",
          state: this.snapshot(includeEnemies, options, session.playerId),
        } satisfies ServerMessage),
      );
    }
  }

  private broadcast(message: ServerMessage): void {
    const payload = JSON.stringify(message);
    for (const session of this.sessions.values()) {
      this.sendPayload(session, payload);
    }
  }

  private send(playerId: string, message: ServerMessage): void {
    const session = this.sessions.get(playerId);
    if (session) {
      this.sendPayload(session, JSON.stringify(message));
    }
  }

  private sendError(playerId: string, code: string, message: string): void {
    this.send(playerId, { type: "error", code, message });
  }

  private sendPayload(session: PlayerSession, payload: string): void {
    if (!session.socket || session.socket.readyState !== SOCKET_OPEN) {
      return;
    }
    session.socket.send(payload);
    session.bytesSinceSample += new TextEncoder().encode(payload).byteLength;
  }

  private sampleMetrics(windowMs: number): void {
    const connected = [...this.sessions.values()].filter(
      (session) => session.socket,
    );
    for (const session of connected) {
      session.lastKbps =
        windowMs > 0 ? (session.bytesSinceSample * 8) / windowMs : 0;
      session.bytesSinceSample = 0;
    }
    const sorted = [...this.tickSamples].sort((a, b) => a - b);
    const p95Index = Math.max(0, Math.ceil(sorted.length * 0.95) - 1);
    this.metrics = {
      tickP95Ms: round(sorted[p95Index] ?? 0, 3),
      outgoingKbpsPerClient: round(
        connected.length > 0
          ? connected.reduce((sum, session) => sum + session.lastKbps, 0) /
              connected.length
          : 0,
        2,
      ),
      enemyCount: this.simulation.enemies.size,
      abilityEntityCount: this.simulation.abilityEntities.size,
    };
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : 0));
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function createRandomId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `player-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function createRandomToken(): string {
  const bytes = new Uint8Array(24);
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join(
    "",
  );
}
